import { Controller } from '@hotwired/stimulus'
import { showFlash, showFlashHTML } from '../utils/flash_messages'

/**
 * CartController
 * 
 * Handles cart operations including adding items to cart,
 * updating cart badge counts, and displaying cart modals.
 * 
 * Uses pure Stimulus with fetch API for AJAX requests.
 */
export default class extends Controller {
  static values = {
    checkoutText: String,
    addToCartUrl: String,
    boxValue: String
  }

  connect() {
    // No event listeners needed - we handle form submission via data-action
  }

  /**
   * Submit add to cart form via fetch
   * Collects all form data and sends to server with CSRF token
   */
  submitAddToCart(event) {
    event.preventDefault()
    
    const formData = this.getFormData()
    const csrfToken = this.getCsrfToken()
    
    if (!csrfToken) {
      this.showError('Security token missing. Please refresh the page and try again.')
      return
    }
    
    this.sendAddToCartRequest(formData, csrfToken)
  }

  /**
   * Get CSRF token from meta tag
   * @returns {string|null} CSRF token or null if not found
   */
  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  /**
   * Collect all form data from the putup list form
   * @returns {FormData} Form data with all inputs
   */
  getFormData() {
    const formData = new FormData()
    
    // Get all input values from the container
    const inputs = this.element.querySelectorAll('input, select, textarea')
    
    inputs.forEach(input => {
      if (input.name) {
        if (input.type === 'checkbox' || input.type === 'radio') {
          if (input.checked) {
            formData.append(input.name, input.value)
          }
        } else {
          formData.append(input.name, input.value)
        }
      }
    })
    
    return formData
  }

  /**
   * Send add to cart request via fetch
   * @param {FormData} formData - Form data to send
   * @param {string} csrfToken - CSRF token for security
   */
  async sendAddToCartRequest(formData, csrfToken) {
    try {
      const response = await fetch(this.addToCartUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: formData,
        credentials: 'same-origin'
      })
      
      // Check content type before parsing
      const contentType = response.headers.get('content-type')
      const isJson = contentType && contentType.includes('application/json')
      
      if (!response.ok) {
        // Handle non-JSON error responses (like HTML error pages)
        if (!isJson) {
          this.handleError({ error: 'Server error occurred' }, response.status)
          return
        }
        const data = await response.json()
        this.handleError(data, response.status)
        return
      }
      
      if (!isJson) {
        this.showError('Unexpected response format from server.')
        return
      }
      
      const data = await response.json()
      this.handleSuccess(data)
    } catch (error) {
      console.error('Add to cart error:', error)
      this.showError('An unexpected error occurred. Please try again.')
    }
  }

  /**
   * Handle successful response from server
   * @param {Object} data - Response data from server
   */
  handleSuccess(data) {
    if (data.modalHtml) {
      this.updateModalContent(data.modalHtml)
      this.showModal()
    }
    
    if (data.cartCount !== undefined) {
      this.updateCartBadge(data.cartCount)
    }
    
    if (data.checkoutUrl) {
      this.ensureCheckoutButton(data.checkoutUrl, data.checkoutText)
    }
    
    if (data.flashMessage && data.flashMessage.content) {
      this.showFlashMessage(data.flashMessage.content)
    }
  }

  /**
   * Handle error response from server
   * @param {Object} data - Error response data
   * @param {number} status - HTTP status code
   */
  handleError(data, status) {
    let errorMessage = 'An error occurred while adding to cart'
    
    if (data && data.error) {
      errorMessage = data.error
    } else if (status === 422) {
      errorMessage = 'Invalid cart data. Please check your selections.'
    } else if (status === 503) {
      errorMessage = 'Pricing service temporarily unavailable. Please try again.'
    }
    
    this.showError(errorMessage)
  }

  /**
   * Update the modal content with server-rendered HTML
   * @param {string} html - The HTML content to display in the modal
   */
  updateModalContent(html) {
    const contentElement = document.getElementById('edit-modal-content')
    if (contentElement) {
      contentElement.innerHTML = html
    }
  }

  /**
   * Show the cart modal using Bootstrap 5 API
   */
  showModal() {
    const modalElement = document.getElementById('cart-modal')
    if (modalElement && window.bootstrap) {
      const modal = new window.bootstrap.Modal(modalElement)
      modal.show()
    } else {
      console.error('Cart modal element or Bootstrap not found')
    }
  }

  /**
   * Update the cart badge count in the navigation
   * @param {number} count - Number of items in cart
   */
  updateCartBadge(count) {
    if (count <= 0) return

    const displayCount = count > 99 ? '99+' : count
    const existingBadges = document.querySelectorAll('.cart-items')

    if (existingBadges.length > 0) {
      existingBadges.forEach(badge => {
        badge.textContent = displayCount
      })
    } else {
      this.createCartBadge(displayCount)
    }
  }

  /**
   * Create a new cart badge if one doesn't exist
   * @param {string|number} count - The count to display
   */
  createCartBadge(count) {
    const cartElements = document.querySelectorAll('.shop-cart')
    cartElements.forEach(cartElement => {
      const badge = document.createElement('span')
      badge.className = 'badge badge bg-danger font-roboto cart-items font-size-13'
      badge.textContent = count
      cartElement.appendChild(badge)
    })
  }

  /**
   * Ensure checkout button exists in the container
   * @param {string} checkoutUrl - URL for the checkout page
   * @param {string} checkoutText - Text for checkout button
   */
  ensureCheckoutButton(checkoutUrl, checkoutText) {
    const containers = document.querySelectorAll('.checkout-container')
    if (!containers.length) return

    containers.forEach(container => {
      const existingButton = container.querySelector('.checkout-button')
      if (existingButton) return

      const checkoutButton = document.createElement('a')
      checkoutButton.href = checkoutUrl
      checkoutButton.className = 'btn btn-color checkout-button text-decoration-none'
      checkoutButton.textContent = checkoutText || this.checkoutTextValue || 'Checkout'
      container.appendChild(checkoutButton)
    })
  }

  /**
   * Display flash messages using centralized utility
   * @param {string} content - Flash message HTML content
   */
  showFlashMessage(content) {
    showFlashHTML(content)
  }

  /**
   * Show error message to user
   * @param {string} message - Error message to display
   */
  showError(message) {
    showFlash({ type: 'danger', message })
  }
}
